import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, SaveIcon, CheckIcon } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Modal } from '../../../components/ui/Modal';
import { TransactionMode, VoucherFormData } from '../../../types/voucher.types';
import { voucherService } from '../../../services/voucher.service';
import { bankAccountService } from '../../../services/bank-account.service';
import { BankAccount } from '../../../types/bank-account.types';
export function PaymentVoucherCreate() {
  const navigate = useNavigate();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [saveAction, setSaveAction] = useState<'draft' | 'complete'>('draft');
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [selectedBankName, setSelectedBankName] = useState<string>('');
  const [filteredAccounts, setFilteredAccounts] = useState<BankAccount[]>([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState<BankAccount | null>(null);
  const [formData, setFormData] = useState<VoucherFormData>({
    voucherType: 'CPV' as any,
    date: new Date().toISOString().split('T')[0],
    transactionMode: TransactionMode.CASH,
    lines: [{
      accountId: '',
      debit: 0,
      credit: 0,
      description: ''
    }]
  });
  useEffect(() => {
    loadBankAccounts();
  }, []);
  const loadBankAccounts = async () => {
    try {
      const response = await bankAccountService.getBankAccounts({
        status: 'Active' as any
      });
      setBankAccounts(response.data);
    } catch (error) {
      console.error('Failed to load bank accounts:', error);
    }
  };
  const handleBankNameChange = (bankName: string) => {
    setSelectedBankName(bankName);
    const filtered = bankAccounts.filter(acc => acc.bankName === bankName);
    setFilteredAccounts(filtered);
    setSelectedBankAccount(null);
    setFormData({
      ...formData,
      bankAccountId: ''
    });
  };
  const handleAccountNoChange = (accountId: string) => {
    const account = filteredAccounts.find(a => a.id === accountId);
    setSelectedBankAccount(account || null);
    setFormData({
      ...formData,
      bankAccountId: accountId
    });
  };
  const uniqueBankNames = Array.from(new Set(bankAccounts.map(acc => acc.bankName)));
  const transactionModes = [{
    value: 'Cash',
    label: 'Cash'
  }, {
    value: 'Bank',
    label: 'Bank'
  }];
  const mockAccounts = [{
    value: '1',
    label: '1100 - Cash'
  }, {
    value: '2',
    label: '1200 - Bank Account'
  }, {
    value: '3',
    label: '2000 - Accounts Payable'
  }, {
    value: '4',
    label: '5000 - Operating Expenses'
  }];
  const mockParties = [{
    value: 'party1',
    label: 'ABC Suppliers Ltd'
  }, {
    value: 'party2',
    label: 'XYZ Services Inc'
  }];
  const mockCostCenters = [{
    value: 'cc1',
    label: 'Operations'
  }, {
    value: 'cc2',
    label: 'Administration'
  }];
  const mockProjects = [{
    value: 'proj1',
    label: 'Infrastructure Project'
  }, {
    value: 'proj2',
    label: 'Education Initiative'
  }];
  // Check if Bank mode is selected - use string comparison
  const isBankMode = formData.transactionMode === 'Bank' || formData.transactionMode === TransactionMode.BANK;
  const addLine = () => {
    setFormData({
      ...formData,
      lines: [...formData.lines, {
        accountId: '',
        debit: 0,
        credit: 0,
        description: ''
      }]
    });
  };
  const removeLine = (index: number) => {
    if (formData.lines.length > 1) {
      setFormData({
        ...formData,
        lines: formData.lines.filter((_, i) => i !== index)
      });
    }
  };
  const updateLine = (index: number, field: string, value: any) => {
    const updated = [...formData.lines];
    updated[index] = {
      ...updated[index],
      [field]: value
    };
    setFormData({
      ...formData,
      lines: updated
    });
  };
  const handleSubmit = async (action: 'draft' | 'complete') => {
    const totalDebit = formData.lines.reduce((sum, line) => sum + (Number(line.debit) || 0), 0);
    if (totalDebit === 0) {
      alert('Please enter debit amounts');
      return;
    }
    if (isBankMode && (!selectedBankName || !formData.bankAccountId)) {
      alert('Please select bank name and account number');
      return;
    }
    setSaveAction(action);
    setShowConfirmModal(true);
  };
  const handleConfirm = async () => {
    try {
      const voucher = await voucherService.createVoucher(formData);
      if (saveAction === 'complete') {
        await voucherService.completeVoucher(voucher.id);
      }
      navigate('/voucher/payment');
    } catch (error: any) {
      alert(error.message);
    } finally {
      setShowConfirmModal(false);
    }
  };
  return <div className="min-h-screen bg-gray-50 p-6" data-id="element-1774">
      <div className="max-w-6xl mx-auto space-y-6" data-id="element-1775">
        <div className="flex items-center gap-4" data-id="element-1776">
          <Button variant="ghost" onClick={() => navigate('/voucher/payment')} data-id="element-1777">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-1778" />
          </Button>
          <div data-id="element-1779">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-1780">
              Create Payment Voucher
            </h1>
            <p className="text-sm text-gray-600 mt-1" data-id="element-1781">
              Record cash or bank payments
            </p>
          </div>
        </div>

        <form onSubmit={e => {
        e.preventDefault();
        handleSubmit('draft');
      }} className="bg-white rounded-lg border p-6 space-y-6" data-id="element-1782">
          <div data-id="element-1783">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1784">
              Basic Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1785">
              <Input label="Transaction Date" type="date" required value={formData.date} disabled helperText="System current date" data-id="element-1786" />

              <Select label="Transaction Mode" required options={transactionModes} value={formData.transactionMode} onChange={e => {
              setFormData({
                ...formData,
                transactionMode: e.target.value as any,
                bankAccountId: ''
              });
              setSelectedBankName('');
              setFilteredAccounts([]);
              setSelectedBankAccount(null);
            }} data-id="element-1787" />

              <div data-id="element-1788">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1789">
                  Party
                </label>
                <input list="parties" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500" value={formData.partyName || ''} onChange={e => setFormData({
                ...formData,
                partyName: e.target.value
              })} placeholder="Select or type party name" data-id="element-1790" />
                <datalist id="parties" data-id="element-1791">
                  {mockParties.map(party => <option key={party.value} value={party.label} data-id="element-1792" />)}
                </datalist>
                <p className="text-xs text-gray-500 mt-1" data-id="element-1793">
                  Select from list or type custom name
                </p>
              </div>

              <Input label="Reference Number" value={formData.referenceNo} onChange={e => setFormData({
              ...formData,
              referenceNo: e.target.value
            })} placeholder="Optional reference" data-id="element-1794" />

              <Select label="Cost Center" options={[{
              value: '',
              label: 'Select Cost Center'
            }, ...mockCostCenters]} value={formData.costCenterId} onChange={e => setFormData({
              ...formData,
              costCenterId: e.target.value
            })} data-id="element-1795" />

              <Select label="Project" options={[{
              value: '',
              label: 'Select Project'
            }, ...mockProjects]} value={formData.projectId} onChange={e => setFormData({
              ...formData,
              projectId: e.target.value
            })} data-id="element-1796" />
            </div>
          </div>

          {isBankMode && <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-1797">
              <h4 className="text-sm font-semibold text-blue-900 mb-4" data-id="element-1798">
                Bank Details
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1799">
                <Select label="Bank Name" required options={[{
              value: '',
              label: 'Select Bank Name'
            }, ...uniqueBankNames.map(name => ({
              value: name,
              label: name
            }))]} value={selectedBankName} onChange={e => handleBankNameChange(e.target.value)} helperText="Select bank name first" data-id="element-1800" />

                <Select label="Account No" required options={[{
              value: '',
              label: 'Select Account No'
            }, ...filteredAccounts.map(acc => ({
              value: acc.id,
              label: `${acc.accountNo} - ${acc.branch}`
            }))]} value={formData.bankAccountId} onChange={e => handleAccountNoChange(e.target.value)} disabled={!selectedBankName} helperText={selectedBankName ? 'Select account number' : 'Select bank name first'} data-id="element-1801" />

                {selectedBankAccount && <div className="md:col-span-2" data-id="element-1802">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1803">
                      Branch
                    </label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-900" data-id="element-1804">
                      {selectedBankAccount.branch}
                    </div>
                  </div>}
              </div>
            </div>}

          <div data-id="element-1805">
            <div className="flex items-center justify-between mb-4" data-id="element-1806">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-1807">
                Transaction Lines
              </h3>
              <Button type="button" size="sm" onClick={addLine} data-id="element-1808">
                Add Line
              </Button>
            </div>

            <div className="border rounded-lg overflow-hidden" data-id="element-1809">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-1810">
                <thead className="bg-gray-50" data-id="element-1811">
                  <tr data-id="element-1812">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1813">
                      Account
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1814">
                      Description
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-1815">
                      Debit
                    </th>
                    <th className="px-4 py-3 w-16" data-id="element-1816"></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1817">
                  {formData.lines.map((line, index) => <tr key={index} data-id="element-1818">
                      <td className="px-4 py-3" data-id="element-1819">
                        <Select options={[{
                      value: '',
                      label: 'Select Account'
                    }, ...mockAccounts]} value={line.accountId} onChange={e => updateLine(index, 'accountId', e.target.value)} className="min-w-[200px]" data-id="element-1820" />
                      </td>
                      <td className="px-4 py-3" data-id="element-1821">
                        <Input value={line.description} onChange={e => updateLine(index, 'description', e.target.value)} placeholder="Description" data-id="element-1822" />
                      </td>
                      <td className="px-4 py-3" data-id="element-1823">
                        <Input type="number" value={line.debit} onChange={e => updateLine(index, 'debit', Number(e.target.value))} className="text-right" min="0" step="0.01" data-id="element-1824" />
                      </td>
                      <td className="px-4 py-3" data-id="element-1825">
                        {formData.lines.length > 1 && <button type="button" onClick={() => removeLine(index)} className="text-red-600 hover:text-red-800" data-id="element-1826">
                            ×
                          </button>}
                      </td>
                    </tr>)}
                </tbody>
                <tfoot className="bg-gray-50" data-id="element-1827">
                  <tr data-id="element-1828">
                    <td colSpan={2} className="px-4 py-3 text-right font-semibold text-gray-900" data-id="element-1829">
                      Total Debit:
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-gray-900" data-id="element-1830">
                      $
                      {formData.lines.reduce((sum, line) => sum + (Number(line.debit) || 0), 0).toLocaleString()}
                    </td>
                    <td data-id="element-1831"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1832">
            <div data-id="element-1833">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1834">
                Description
              </label>
              <textarea className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500" rows={3} value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} placeholder="Enter voucher description..." data-id="element-1835" />
            </div>

            <div data-id="element-1836">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1837">
                Narration
              </label>
              <textarea className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500" rows={3} value={formData.narration} onChange={e => setFormData({
              ...formData,
              narration: e.target.value
            })} placeholder="Additional notes..." data-id="element-1838" />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t" data-id="element-1839">
            <Button type="button" variant="secondary" onClick={() => navigate('/voucher/payment')} data-id="element-1840">
              Cancel
            </Button>
            <Button type="submit" variant="secondary" data-id="element-1841">
              <SaveIcon className="w-4 h-4 mr-2" data-id="element-1842" />
              Save as Draft
            </Button>
            <Button type="button" variant="primary" onClick={() => handleSubmit('complete')} data-id="element-1843">
              <CheckIcon className="w-4 h-4 mr-2" data-id="element-1844" />
              Save & Complete
            </Button>
          </div>
        </form>
      </div>

      <Modal isOpen={showConfirmModal} onClose={() => setShowConfirmModal(false)} title={saveAction === 'draft' ? 'Save as Draft' : 'Save & Complete'} footer={<>
            <Button variant="secondary" onClick={() => setShowConfirmModal(false)} data-id="element-1846">
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirm} data-id="element-1847">
              Confirm
            </Button>
          </>} data-id="element-1845">
        <p className="text-gray-700" data-id="element-1848">
          {saveAction === 'draft' ? 'Save this voucher as draft? You can edit it later.' : 'Complete this voucher? It will be locked for editing and sent for approval.'}
        </p>
        <div className="mt-4 p-3 bg-gray-50 rounded text-sm" data-id="element-1849">
          <div data-id="element-1850">
            <strong data-id="element-1851">Date:</strong> {formData.date}
          </div>
          <div data-id="element-1852">
            <strong data-id="element-1853">Mode:</strong> {formData.transactionMode}
          </div>
          {selectedBankAccount && <>
              <div data-id="element-1854">
                <strong data-id="element-1855">Bank:</strong> {selectedBankAccount.bankName}
              </div>
              <div data-id="element-1856">
                <strong data-id="element-1857">Account:</strong> {selectedBankAccount.accountNo}
              </div>
            </>}
          <div data-id="element-1858">
            <strong data-id="element-1859">Total:</strong> $
            {formData.lines.reduce((sum, line) => sum + (Number(line.debit) || 0), 0).toLocaleString()}
          </div>
        </div>
      </Modal>
    </div>;
}